import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { ASSETS } from "@/lib/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewPage() {
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!ref.trim()) return toast.error("Please enter your booking reference.");
    if (!name.trim()) return toast.error("Please enter your name.");
    if (text.trim().length < 3) return toast.error("Please write a short review.");
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, { booking_ref: ref.trim(), name: name.trim(), text: text.trim() });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[100svh] flex items-center justify-center px-6 py-16 relative" data-testid="review-page">
      <div className="absolute inset-0 spotlight pointer-events-none" />
      <div className="relative z-10 w-full max-w-lg ornate-frame bg-[var(--surface)] p-8 sm:p-12">
        <div className="flex flex-col items-center text-center mb-8">
          <img src={ASSETS.crest} alt="" className="w-16 mb-4 rounded-full" />
          <p className="uppercase tracking-[0.35em] text-[11px] text-[var(--gold)]">Beer Explained</p>
          <h1 className="font-display text-4xl sm:text-5xl text-[var(--cream)] mt-3">Leave a review</h1>
        </div>

        {done ? (
          <div className="text-center" data-testid="review-thanks">
            <div className="w-14 h-14 mx-auto rounded-full border border-[var(--gold)] flex items-center justify-center mb-6">
              <Check className="w-6 h-6 text-[var(--gold)]" />
            </div>
            <p className="font-serif-alt text-xl text-[var(--cream)]">Thank you for your review!</p>
            <p className="font-body text-sm text-[var(--muted)] mt-3">It will appear on the site once it has been approved.</p>
            <Link to="/" data-testid="review-back-home" className="inline-block mt-8 rounded-full border border-[var(--gold)] text-[var(--gold)] px-8 py-3 uppercase tracking-[0.2em] text-xs font-semibold hover:bg-[var(--gold)] hover:text-[var(--bg)] transition-colors duration-300">
              Back to the site
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6">
            <div>
              <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-2">Booking reference</label>
              <input data-testid="review-ref-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="BEX-XXXXXX" className="field-input" required />
            </div>
            <div>
              <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-2">Your name</label>
              <input data-testid="review-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" className="field-input" required />
            </div>
            <div>
              <label className="block uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] mb-2">Your review</label>
              <textarea data-testid="review-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="How was the evening?" className="field-input resize-none" required />
            </div>
            <button type="submit" data-testid="review-submit-btn" disabled={loading}
              className="w-full rounded-full bg-[var(--gold)] text-[var(--bg)] py-4 uppercase tracking-[0.2em] text-xs font-semibold flex items-center justify-center gap-2 hover:bg-[var(--cream)] transition-colors duration-300 disabled:opacity-50">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Submit review"}
            </button>
            <p className="text-center font-body text-[11px] text-[var(--muted)]">Reviews are checked before they appear on the site.</p>
          </form>
        )}
      </div>
    </main>
  );
}
